import {HttpErrorResponse} from '@angular/common/http';
import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';

import {ApiDetail} from '../api-detail';
import {ApisService} from '../apis.service';
import {Applications} from '../applications';
import {ApplicationDetail} from '../application-detail';
import {SubscriptionItem} from '../subscription-item';
import {SubscriptionManageEventType} from './subscription-manage-event-type';

const SUBSCRIPTION_EVENT_SUBSCRIBED = 'Subscribed';
const SUBSCRIPTION_EVENT_CANCELLED = 'Cancelled';

@Component({
  selector: 'aqx-subscription-manage',
  templateUrl: './subscription-manage.component.html',
  styleUrls: ['./subscription-manage.component.scss']
})
export class SubscriptionManageComponent implements OnInit {
  @Input() apiDetail: ApiDetail;
  @Input() isSubscribing: boolean;
  @Input() isUnsubscribing: boolean;
  @Input() subscriptionId: string;
  @Output() messageEvent = new EventEmitter<object>();

  title: string;
  applications: Applications;
  suggestedApplicationNameInfo;
  subscriptionManageForm: FormGroup;
  applicationCreationTypes = {
    applicationExisting: {
      label: 'Existing Application', value: 'applicationExisting'
    },
    applicationNew: {
      label: 'New Application', value: 'applicationNew'
    }
  };

  constructor(private route: ActivatedRoute, private apisService: ApisService) {
  }

  async ngOnInit() {
    this.title = 'Confirm ' + (this.isSubscribing ? 'Subscription' : 'Unsubscribe');

    await this.getApplications();
    if (this.applications) {
      this.suggestedApplicationNameInfo = await this.suggestNewApplicationName();
      this.subscriptionManageForm = this.createFormGroup();
    }
  }

  private createFormGroup() {
    let applicationExistingDefault;
    this.applications.list.forEach((application) => {
      if (application.name === 'DefaultApplication') {  // We assume this will always be present
        applicationExistingDefault = application;
      }
    });

    return new FormGroup({
      applicationExistingSelected: new FormControl(applicationExistingDefault),
      applicationCreationTypeSelected: new FormControl(this.applicationCreationTypes.applicationExisting),
      applicationNew: new FormGroup({
        applicationName: new FormControl(this.suggestedApplicationNameInfo.applicationName),
        applicationDescription: new FormControl(this.suggestedApplicationNameInfo.applicationDescription),
        applicationCallbackUrl: new FormControl(this.suggestedApplicationNameInfo.applicationCallbackUrl)
      })
    });
  }

  private async getApplications() {
      // http://www.damirscorner.com/blog/posts/20170127-Angular2TutorialWithAsyncAndAwait.html
    await this.apisService.getApplications().toPromise()
      .then(async response  => {
        this.applications = response;
      })
      .catch((httpErrorResponse: HttpErrorResponse) => {
        console.log(httpErrorResponse);
        this.cancel();
      });
  }

  async suggestNewApplicationName() {
      let applicationNameSuffix = 0;
      let applicationName = this.apiDetail.name + 'Application';
      let applicationDescription = `Description for ${this.apiDetail.name} Application`;
      await this.applications.list.forEach(async (application) => {
        const pos = application.name.indexOf(applicationName);
        if (pos > -1) {
          const suffix = applicationName.substring(applicationName.length);
          const suffixNum = +suffix; // cast to number
          if (suffixNum >= applicationNameSuffix) {
            applicationNameSuffix = suffixNum + 1;
          }
        }
      });
      if (applicationNameSuffix > 0) {
        applicationName = applicationName + +applicationNameSuffix;
        applicationDescription = applicationDescription + +applicationNameSuffix;
      }
      return ({applicationName, applicationDescription});
  }

  isApplicationCreationTypeNew() {
    return this.subscriptionManageForm.value.applicationCreationTypeSelected === this.applicationCreationTypes.applicationNew;
  }

  async onSubmit() {
    let eventType: SubscriptionManageEventType;
    if (this.isSubscribing) {
      await this.subscribe();
      eventType = SubscriptionManageEventType.subscribed;
    } else {
      await this.unsubscribe();
      eventType = SubscriptionManageEventType.unsubscribed;
    }
    this.messageEvent.emit({eventType});
  }

  async subscribe() {
    let applicationDetail: ApplicationDetail;
    if (this.isApplicationCreationTypeNew()) {
      await this.apisService.createApplication(
        {
          name: this.subscriptionManageForm.value.applicationNew.applicationName.trim(),
          description: this.subscriptionManageForm.value.applicationNew.applicationDescription ?
            this.subscriptionManageForm.value.applicationNew.applicationDescription.trim() : '',
          callbackUrl: this.subscriptionManageForm.value.applicationNew.applicationCallbackUrl ?
            this.subscriptionManageForm.value.applicationNew.applicationCallbackUrl.trim() : '',
          throttlingTier: 'Unlimited' // TODO
        }).toPromise()
        .then((response: ApplicationDetail) => {
          applicationDetail = response;
        })
        .catch((httpErrorResponse: HttpErrorResponse) => {
          console.log(httpErrorResponse);
          this.cancel();
        });
    } else {
      await this.apisService.getApplicationDetail(this.subscriptionManageForm.value.applicationExistingSelected.applicationId).toPromise()
        .then((response: ApplicationDetail) => {
          applicationDetail = response;
        })
        .catch((httpErrorResponse: HttpErrorResponse) => {
          console.log(httpErrorResponse);
          this.cancel();
        });
    }

    if (applicationDetail) { // no error
      // Step 1 of 2: Generate as many keys as are in the environmentList of the apiDetail.
      // Note that the default application "DefaultApplication" does not have any keys generated by default.
      this.apiDetail.environmentList.forEach(async keyType => {
        if (!applicationDetail.keys.find(key => key.keyType === keyType)) {
          await this.apisService.createApplicationKeys(applicationDetail.applicationId,
          {
            validityTime: '3600',
            keyType,
            accessAllowDomains: ['ALL'],
            scopes: ['am_application_scope', 'default'],
            supportedGrantTypes:
              ['urn:ietf:params:oauth:grant-type:saml2-bearer', 'iwa:ntlm', 'refresh_token', 'client_credentials', 'password']
          }).toPromise()
            .catch((httpErrorResponse: HttpErrorResponse) => {
              console.log(httpErrorResponse);
              this.cancel();
            });
        }
      });

      // Step 2 of 2: Create the subscription
      await this.apisService.createSubscription({
        tier: this.apiDetail.tiers[0],
        apiIdentifier: this.apiDetail.id,
        applicationId: applicationDetail.applicationId
      }).toPromise()
        .then((response: SubscriptionItem) => {
          // let subscription: SubscriptionItem;
          // subscription = response;
        })
        .catch((httpErrorResponse: HttpErrorResponse) => {
          console.log(httpErrorResponse);
          this.cancel();
        });
    }
  }


  async unsubscribe() {
    await this.apisService.deleteSubscription(this.subscriptionId).toPromise()
      .then((response) => {
      })
      .catch((httpErrorResponse: HttpErrorResponse) => {
        console.log(httpErrorResponse);
        this.cancel();
      });
  }

  cancel() {
    this.messageEvent.emit(
      {name: SUBSCRIPTION_EVENT_CANCELLED}
    );
  }
}
